import { HttpService } from './store/http/http.service';
import { MSEditorService } from './components/ms-editor/ms-editor.service';
import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { ServerService } from './server.service';

// Components
import { AppComponent } from './app.component';

// Services
import { KeyHandlerService } from './services/keyhandler/keyhandler.service';
import { CanvasService } from './services/canvas/canvas.service';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { FormsModule } from '@angular/forms';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { BaseReducer } from './store/reducer';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreService } from './store/store.service';
import { StoreEffects } from './store/effect/effect';

import { FlexLayoutModule } from '@angular/flex-layout';
import { MaterialModule } from './material.module';
import { TypeService } from './components/data-editor/type.service';

import { ComponentsModule } from './components/components.module';
import { MetaScriptModule } from './ms/metascript.module';
import { HttpCacheInterceptorModule } from '@ngneat/cashew';

import { SignUpComponent } from './sign-up/sign-up.component';
import { SignInComponent } from './sign-in/sign-in.component';
import { ProfileComponent } from './profile/profile.component';
import { AppRoutingModule } from './app-routing.module';
import { environment } from '../environments/environment';
import { EditorModule } from './element-editor/element-editor.module';
import { AuthenticationModule } from './authentication/authentication.module';
import { OrganisationModule } from './organisation/organisation.module';
import { ProjectModule } from './projects/project.module';
import { UserModule } from './user/user.module';
import { HomeModule } from './home/home.module';
import { LabModule } from './lab/lab.module';
import { APIConfig } from './api/api-config.provider';
import { NewProjectComponentComponent } from './new/new-project-component/new-project-component.component';
import { ProjectOverviewComponent } from './project-overview/project-overview.component';
import { VideoControlsComponent } from './project-overview/video-controls/video-controls.component';
import { SceneInputComponent } from './project-overview/scene-input/scene-input.component';
import { VideoPreviewComponent } from './project-overview/video-preview/video-preview.component';
import { MediaPreviewModalComponent } from './project-overview/media-preview-modal/media-preview-modal.component';
import { UtilComponentModule } from './components/util/util-component.module';
import { DirectivesModule } from './directives/directives.module';
import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ColorEditorOverlayComponent } from './shared/overlays/color-editor-overlay/color-editor-overlay.component';
import { ProjectPreviewLoadingComponent } from './shared/project-preview-loading/project-preview-loading.component';
import { VideoEditPromptComponent } from './components/video-edit-prompt/video-edit-prompt.component';
import { BackgroundSettingsModalComponent } from './project-overview/background-settings-modal/background-settings-modal.component';
import { LayoutSettingsModalComponent } from './project-overview/layout-settings-modal/layout-settings-modal.component';
import { TextEditModalComponent } from './project-overview/text-edit-modal/text-edit-modal.component';
import { ImageUploadModalComponent } from './project-overview/image-upload-modal/image-upload-modal.component';
import { MediaUploadModalComponent } from './project-overview/media-upload-modal/media-upload-modal.component';
import { VideoSettingTabsComponent } from './project-overview/video-setting-tabs/video-setting-tabs.component';
import { BackgroundSettingsComponent } from './project-overview/background-settings/background-settings.component';
import { BackgroundDefinitionsJsonModalComponent } from './project-overview/background-settings/background-definitions-json-modal.component';
import { AnimationKeyComponent } from './project-overview/animation-key/animation-key.component';
import { TextAnimationEditorComponent } from './project-overview/text-animation-editor/text-animation-editor.component';
import { TextAnimationStepModalComponent } from './project-overview/text-animation-editor/text-animation-step-modal.component';
import { TextAnimationStepJsonModalComponent } from './project-overview/text-animation-editor/text-animation-step-json-modal.component';
import { FileProjectSettingsComponent } from './project-overview/file-key-json-editor/file-project-settings.component';
import { FileProjectDefinitionsJsonModalComponent } from './project-overview/file-key-json-editor/file-project-definitions-json-modal.component';
import { VideoQuestionnaireComponent } from './new/video-questionnaire/video-questionnaire.component';
import { UserStoryChatbotComponent } from './new/user-story-chatbot/user-story-chatbot.component';
import { LayoutSettingsComponent } from './project-overview/layout-settings/layout-settings.component';
import { ControlerPanelModule } from './element-editor/control-panel/control-panel.module';
import { DraggableComponent } from './project-overview/video-preview/draggable-component';
import { PreviewsModalComponent } from './project-overview/previews-modal/previews-modal.component';

@NgModule({
  declarations: [
    AppComponent,
    TextAnimationEditorComponent,
    TextAnimationStepModalComponent,
    TextAnimationStepJsonModalComponent,
    SignUpComponent,
    SignInComponent,
    ProfileComponent,
    ColorEditorOverlayComponent,
    NewProjectComponentComponent,
    TextEditModalComponent,
    ProjectOverviewComponent,
    BackgroundSettingsModalComponent,
    LayoutSettingsModalComponent,
    ImageUploadModalComponent,
    MediaUploadModalComponent,
    ProjectPreviewLoadingComponent,
    VideoControlsComponent,
    SceneInputComponent,
    VideoEditPromptComponent,
    VideoSettingTabsComponent,
    AnimationKeyComponent,
    FileProjectSettingsComponent,
    FileProjectDefinitionsJsonModalComponent,
    BackgroundSettingsComponent,
    BackgroundDefinitionsJsonModalComponent,
    VideoQuestionnaireComponent,
    UserStoryChatbotComponent,
    LayoutSettingsComponent,
    VideoPreviewComponent,
    MediaPreviewModalComponent,
    DraggableComponent,
    PreviewsModalComponent,
  ],
  imports: [
    ComponentsModule,
    UtilComponentModule,
    BrowserAnimationsModule,
    BrowserModule,
    DirectivesModule,
    FlexLayoutModule,
    FormsModule,
    HttpClientModule,
    HttpCacheInterceptorModule.forRoot(),
    MaterialModule,
    // ShapeAttributePanelModule,
    EffectsModule.forRoot([StoreEffects]),
    StoreModule.forRoot(BaseReducer),
    !environment.production
      ? StoreDevtoolsModule.instrument({ maxAge: 25 })
      : [],
    MetaScriptModule,
    AppRoutingModule,
    EditorModule,
    ControlerPanelModule,
    // SidePanelModule,
    AuthenticationModule,
    OrganisationModule,
    ProjectModule,
    UserModule,
    HomeModule,
    LabModule,
    A11yModule,
    DragDropModule,
  ],
  providers: [
    // useHttpCacheLocalStorage,
    CanvasService,
    KeyHandlerService,
    MSEditorService,
    ServerService,
    StoreService,
    TypeService,
    StoreEffects,
    HttpService,
    APIConfig,
    {
      provide: APP_INITIALIZER,
      useFactory: (apiConfig: APIConfig) => () => apiConfig.loadConfig(),
      deps: [APIConfig],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    console.log(
      `App version: ${environment.version} (${
        environment.production ? 'production' : 'development'
      })`,
    );
    // if (!environment.production) {
    //   router.events
    //     .pipe(filter(event => event instanceof NavigationEnd))
    //     .subscribe(event => console.log(activatedRoute.snapshot));
    // }
  }
}
